import moment from 'moment';

import { IViewCard, TableColumn } from '@/components';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import {
  AlcoholMeasurementTypeList,
  CardNetworksList,
  CurrencyCode,
  ExplosiveTypesList,
  IOffenseOption,
  FirearmTypes,
  IOffenseOptionItem,
  PropertyStatus,
  PropertyTypeTag,
  Role,
} from '@/models';
import { FileAcceptedTypes } from '@/pages/Kases/KaseFiles/components/data';
import { preventSubmit } from '@/utils';

export type PropertySourceType = 'kase' | 'cfs';

export const KasePropertyNextPaths = {
  propertyList: ['/cases/new/naratives'],
};

export const KaseArrestsPropertyNextRoutes = ['narratives', 'property'];
export enum KasePropertiesScreens {
  KasePropertyList = 'KasePropertyList',
  KasePropertyEdit = 'KasePropertyEdit',
  KasePropertyView = 'KasePropertyView',
}

// TODO: updated on implementation

export interface IKaseNestedRow {
  brand: string;
  quantity: number;
  measurementType: string;
  estimatedValue: string;
}
export interface KasePropertyRow {
  _id: string;
  number: string;
  propertyTypeTag: string;
  itemNumber: number;
  typeTag: string;
  involvement: string;
  dateLogged: string;
  loggedInfo: string;
  status: string;
  location: string;
  nested?: Array<IKaseNestedRow>;
}

export const KasePropertyEditors: Role[] = [
  Role.AGENCY_ADMIN,
  Role.KASE_APPROVER,
  Role.EVIDENCE_COLLECTOR,
  Role.EVIDENCE_CUSTODIAN,
  Role.EVIDENCE_TRANSPORTER,
];

export const KasePropertyViewers: Role[] = [
  ...KasePropertyEditors,
  Role.RMS_CASES_FULL_ACCESS,
  Role.RMS_CASES_VIEW,
];

export const CustodyActions = {
  AUCTION: 'AUCTION',
  CHECK_IN: 'CHECK_IN',
  CUSTODY_LOGS: 'CUSTODY_LOGS',
  DESTROY: 'DESTROY',
  DISPOSE: 'DISPOSE',
  MOVE_LOCATION: 'MOVE_LOCATION',
  PRINT_CUSTODY_LOGS: 'PRINT_CUSTODY_LOGS',
  PRINT_BULK_CUSTODY_LOGS: 'PRINT_BULK_CUSTODY_LOGS',
  PRINT_LABEL: 'PRINT_LABEL',
  PRINT_LABELS: 'PRINT_LABELS',
  TO_COURT: 'TO_COURT',
  TO_LAB: 'TO_LAB',
  TO_OTHER_AGENCY: 'TO_OTHER_AGENCY',
  TO_OWNER: 'TO_OWNER',
  TO_PERSON: 'TO_PERSON',
} as const;

export type ICustodyAction = keyof typeof CustodyActions;

export type IKasePropertyActionMenuItem = {
  id: string;
  label: string;
  value?: ICustodyAction;
  allowedPropertyStatus?: PropertyStatus[];
  subMenuItems?: IKasePropertyActionMenuItem[];
};

export type IKasePropertyActionMemuMainItem = {
  id: string;
  label: string;
  value?: ICustodyAction;
  allowedPropertyStatus?: PropertyStatus[];
  subMenuItems?: IKasePropertyActionMemuMainItem[];
};

export const KasePropertyActionMemuMainItems: IKasePropertyActionMemuMainItem[] =
  [
    {
      id: 'custody-check-in',
      label: 'Check In',
      value: CustodyActions.CHECK_IN,
      allowedPropertyStatus: [
        PropertyStatus.LOGGED,
        PropertyStatus.IN_POLICE_CUSTODY,
        PropertyStatus.RELEASED_TO_LAB,
        PropertyStatus.RELEASED_TO_COURT,
        PropertyStatus.RELEASED_TO_PERSON,
        PropertyStatus.TRANSFERRED,
      ],
    },
    {
      id: 'custody-check-out',
      label: 'Check Out',
      allowedPropertyStatus: [PropertyStatus.IN_POLICE_CUSTODY],
      subMenuItems: [
        {
          id: 'custody-checkout-to-court',
          label: 'To Court',
          value: CustodyActions.TO_COURT,
        },
        {
          id: 'custody-checkout-to-lab',
          label: 'To Lab',
          value: CustodyActions.TO_LAB,
        },
        {
          id: 'custody-checkout-to-person',
          label: 'To Person',
          value: CustodyActions.TO_PERSON,
        },
        {
          id: 'custody-checkout-to-other-agency',
          label: 'To Other Agency',
          value: CustodyActions.TO_OTHER_AGENCY,
        },
      ],
    },
    {
      id: 'custody-move-location',
      label: 'Move Location',
      value: CustodyActions.MOVE_LOCATION,
      allowedPropertyStatus: [PropertyStatus.IN_POLICE_CUSTODY],
    },
    {
      id: 'custody-final-disposition',
      label: 'Final Disposition',
      allowedPropertyStatus: [
        PropertyStatus.IN_POLICE_CUSTODY,
        PropertyStatus.RELEASED_TO_LAB,
        PropertyStatus.RELEASED_TO_COURT,
        PropertyStatus.RELEASED_TO_PERSON,
        PropertyStatus.TRANSFERRED,
      ],
      subMenuItems: [
        {
          id: 'custody-relase-to-owner',
          label: 'To Owner',
          value: CustodyActions.TO_OWNER,
        },
        {
          id: 'custody-release-dispose',
          label: 'Dispose',
          value: CustodyActions.DISPOSE,
        },
        {
          id: 'custody-release-destroy',
          label: 'Destroy',
          value: CustodyActions.DESTROY,
        },
        {
          id: 'custody-release-auction',
          label: 'Auction',
          value: CustodyActions.AUCTION,
        },
      ],
    },
  ];

export const KasePropertyActionMemuFooterItems: IKasePropertyActionMenuItem[] =
  [
    {
      id: 'custody-view-logs',
      label: 'View Custody Log',
      value: CustodyActions.CUSTODY_LOGS,
    },
    {
      id: 'custody-print-logs',
      label: 'Print Custody Log',
      value: CustodyActions.PRINT_CUSTODY_LOGS,
    },
    {
      id: 'custody-print-label',
      label: 'Print Label',
      value: CustodyActions.PRINT_LABEL,
    },
  ];

export const KasePropertyTableColumns = [
  { key: 'toggle', label: '' },
  { key: 'number', label: 'Property ID' },
  { key: 'name', label: 'Name' },
  { key: 'typeTag', label: 'Property type tag' },
  { key: 'itemNumber', label: 'Item number' },
  { key: 'type', label: 'Type' },
  { key: 'involvement', label: 'Involvement' },
  { key: 'dateLogged', label: 'Date logged' },
  { key: 'loggedBy', label: 'Collected by' },
  { key: 'status', label: 'Status' },
  { key: 'loggedAddress', label: 'Location' },
  { key: 'action', label: '' },
];

export const kasePropertyDataGridCols: DataGridColSettings[] = [
  {
    field: 'number',
    headerName: 'Property ID',
    width: 180,
    visible: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 200,
    visible: true,
  },
  {
    field: 'typeTag',
    headerName: 'Property type tag',
    width: 150,
    visible: true,
  },
  {
    field: 'itemNumber',
    headerName: 'Item number',
    width: 150,
    visible: true,
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 220,
    visible: true,
  },
  {
    field: 'involvement',
    headerName: 'Involvement',
    width: 150,
    visible: true,
  },
  {
    field: 'dateLogged',
    headerName: 'Date logged',
    width: 150,
    visible: true,
  },
  {
    field: 'loggedBy',
    headerName: 'Collected by',
    width: 150,
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    visible: true,
  },
  {
    field: 'loggedAddress',
    headerName: 'Location',
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    visible: true,
  },
];

export const propertyPrintLabelFields = [
  {
    key: 'logged',
    items: [
      { key: 'propertyType', label: 'Property type' },
      { key: 'type', label: 'Type' },
      { key: 'submittedBy', label: 'Submitted by' },
      { key: 'date', label: 'Collected on' },
      { key: 'description', label: 'Description' },
    ],
  },
];

export const CustodyLogsViewFields = [
  {
    leftTitle: '06/22/2024 15:22',
    rightTitle: 'Logged',
    key: 'logged',
    items: [
      { label: 'Collected by', value: 'John Doe' },
      { label: 'Badge number', value: '111111' },
      { label: 'Logged date', value: '06/18/2024' },
      { label: 'Logged time', value: '11:00' },
    ],
  },
  {
    leftTitle: '06/18/2024 15:30',
    rightTitle: 'Infield Transfer',
    key: 'InfieldTransfer',
    items: [
      { label: 'Transferred by', value: 'John Doe' },
      { label: 'Badge number', value: '111111' },
      { label: 'Logged date', value: '06/18/2024' },
      { label: 'Logged time', value: '11:00' },
    ],
  },
  {
    leftTitle: '06/18/2024 21:38',
    rightTitle: 'In Police Custody',
    key: 'InPoliceCustody',
    items: [
      { label: 'Inventory location name', value: '#1233' },
      { label: 'Note', value: 'Lorem ipsum' },
    ],
  },
  {
    leftTitle: '06/19/2024 21:38',
    rightTitle: 'Released to Lab',
    key: 'ReleasedtoLab',
    items: [
      { label: 'Lab name', value: 'Lab123' },
      { label: 'Date expected back', value: '07/01/2024' },
      { label: 'Tracking number', value: '112233' },
      { label: 'Note', value: 'Lorem ipsum' },
    ],
  },
];

export const PropertyFormFields = [
  {
    key: 'generalDetails',
    title: 'General details',
    items: [
      {
        name: 'groupedFields',
        grid: {
          md: 6,
        },
        items: [
          {
            name: 'owner',
            label: 'Item owner',
            type: 'autocomplete',
            items: 'getRelatedMasterNames',
          },
          {
            name: 'targetDispositionDate',
            label: 'Target Disposition Date',
            type: 'date',
          },
          {
            name: 'serialNumber',
            label: 'Serial Number',
            conditions: [PropertyTypeTag.PHYSICAL],
            rules: {
              maxLength: {
                value: 20,
                message:
                  'Serial number should be less than 20 characters long.',
              },
            },
          },
          {
            name: 'value',
            label: 'Value',
            type: 'number',
            conditions: [PropertyTypeTag.VEHICLE, PropertyTypeTag.PHYSICAL],
            rules: {
              maxLength: {
                value: 9,
                message: 'Value is maximum 9 characters long',
              },
            },
          },
          {
            name: 'quantity',
            label: 'Quantity',
            conditions: [PropertyTypeTag.DIGITAL],
          },
        ],
      },

      {
        name: 'isEvidence',
        label: 'Is evidence',
        type: 'switch',
        grid: {
          md: 2,
        },
      },
      {
        name: 'isBiohazard',
        label: 'Bio Hazard',
        conditions: [PropertyTypeTag.VEHICLE, PropertyTypeTag.PHYSICAL],
        type: 'switch',
        grid: {
          md: 2,
        },
      },
      {
        name: 'inPoliceCustody',
        label: 'In Police Custody',
        conditions: [PropertyTypeTag.VEHICLE, PropertyTypeTag.PHYSICAL],
        type: 'switch',
        grid: {
          md: 2,
        },
      },
    ],
  },
  {
    key: 'type',
    title: 'Property type details',
    callback: 'renderConditionalSection',
  },
  {
    key: 'logDetails',
    title: 'Collection details',
    items: [
      {
        name: 'loggedInfo.loggedBy',
        label: 'Collected by',
        type: 'autocomplete',
        items: 'geEvidenceCollectors',
      },
      {
        name: 'loggedInfo.loggedDate',
        label: 'Collected on',
        type: 'datetime',
        maxDate: moment(),
        disableFuture: true,
        grid: {
          md: 3,
        },
      },
      {
        name: 'groupedFields',
        grid: {
          md: 6,
        },
        items: [
          {
            name: 'loggedInfo.loggedAddress',
            label: 'Location collected',
            type: 'address',
            grid: {
              md: 8,
            },
            addressColumnsTemplate: 'minmax(0, 2fr) minmax(180px, 1fr) auto',
          },
          {
            name: 'autofillLocationButton',
            label: 'Collected at incident location',
            type: 'button',
            grid: {
              md: 4,
            },
            sx: {
              marginTop: '25px',
            },
          },
        ],
      },
      {
        name: 'inboundTransferredInfo.transferredBy',
        label: 'Transferred by',
        type: 'autocomplete',
        items: 'geEvidenceTransporter',
      },
      {
        name: 'inboundTransferredInfo.transferredDate',
        label: 'Transferred on',
        type: 'datetime',
        maxDate: moment(),
        disableFuture: true,
      },
      {
        name: 'droppedOffLocation',
        label: 'Check-in inventory location',
        type: 'autocomplete',
        items: 'getInventoryLocations',
      },
    ],
  },
  {
    key: 'relatedItems',
    title: 'Related items',
    items: [
      {
        name: 'relatedOffenses.offenses',
        label: 'Related offense',
        type: 'autocomplete',
        multiple: true,
        limitTags: 2,
        defaultValue: [],
        items: 'relatedOffenses',
        grid: {
          md: 4,
        },
      },
      {
        name: 'relatedMasterNames.masterNames',
        label: 'Related name',
        type: 'autocomplete',
        multiple: true,
        limitTags: 2,
        defaultValue: [],
        items: 'getRelatedMasterNames',
        grid: {
          md: 4,
        },
      },
      {
        name: 'relatedMasterVehicles.masterVehicles',
        label: 'Related vehicle',
        type: 'autocomplete',
        multiple: true,
        limitTags: 2,
        defaultValue: [],
        items: 'getRelatedVehicles',
        grid: {
          md: 4,
        },
      },
      {
        name: 'relatedOffenses.notes',
        label: 'Related offense notes',
        multiline: true,
        revealOnClick: true,
        rules: {
          maxLength: {
            value: 300,
            message: 'Notes should be less than 300 characters.',
          },
        },
        rows: 3,
        grid: {
          md: 4,
        },
      },
      {
        name: 'relatedMasterNames.notes',
        label: 'Related name notes',
        multiline: true,
        revealOnClick: true,
        rules: {
          maxLength: {
            value: 300,
            message: 'Notes should be less than 300 characters.',
          },
        },
        rows: 3,
        grid: {
          md: 4,
        },
      },
      {
        name: 'relatedMasterVehicles.notes',
        label: 'Related vehicle notes',
        multiline: true,
        revealOnClick: true,
        rules: {
          maxLength: {
            value: 300,
            message: 'Notes should be less than 300 characters.',
          },
        },
        rows: 3,
        grid: {
          md: 4,
        },
      },
    ],
  },
  {
    key: 'mediaAndDocuments',
    title: 'Media and documents',
    items: [
      {
        name: 'files',
        type: 'file',
        label: '',
        accept: FileAcceptedTypes,
        grid: {
          md: 12,
          lg: 12,
        },
      },
    ],
  },
];

export const PropertyTypePhysicalFields = (
  PropertyTypePhysicalList: Map<string, IOffenseOptionItem>,
  SourceofDrugData?: IOffenseOption,
  canAddNew = false,
) => {
  if (!PropertyTypePhysicalList.size) {
    return [];
  }
  return [
    {
      key: 'type',
      title: 'Property type details',
      items: [
        {
          name: 'type',
          label: 'Type',
          type: 'autocomplete',
          freeSolo: true,
          showPopupIcon: true,
          clearOnBlur: true,
          addNew: canAddNew,
          items: (() => {
            const grouped = new Map<
              string,
              { value: string; label: string }[]
            >();
            for (const {
              key,
              label,
              category,
            } of PropertyTypePhysicalList.values()) {
              const cat = category || '';
              if (!grouped.has(cat)) grouped.set(cat, []);
              grouped.get(cat)!.push({ value: key ?? '', label });
            }
            const result: { group?: string; label: string; value: string }[] =
              [];
            for (const [cat, items] of grouped) {
              if (cat) result.push({ group: cat, label: cat, value: '' });
              result.push(...items);
            }
            return result;
          })(),
        },
        ...(SourceofDrugData
          ? [
              {
                name: 'drugsDataSource',
                condition: PropertyTypePhysicalList.get('DRUGS_NARCOTICS')?.key,
                label: SourceofDrugData.title,
                type: 'select',
                items: Array.from(SourceofDrugData.items.values()).map(
                  (item) => ({
                    value: item.value,
                    label: item.label,
                  }),
                ),
              },
            ]
          : []),
      ],
    },
    {
      key: 'alcohol',
      condition: PropertyTypePhysicalList.get('ALCOHOL')?.key,
      items: [
        {
          name: 'alcohol.brand',
          label: 'Brand',
          type: 'autocomplete',
          items: 'getAlcoholBrands',
          freeSolo: true,
          showPopupIcon: true,
        },
        {
          name: 'alcohol.quantity',
          label: 'Quantity',
          type: 'number',
        },
        {
          name: 'alcohol.measurementType',
          label: 'Measurement unit',
          type: 'autocomplete',
          items: Object.entries(AlcoholMeasurementTypeList).map(
            ([value, label]) => ({
              value,
              label,
            }),
          ),
        },
      ],
    },
    {
      key: 'creditCard',
      condition: PropertyTypePhysicalList.get('CREDIT_DEBIT_CARDS')?.key,
      items: [
        {
          name: 'creditCard.name',
          label: 'Name on card',
          rules: {
            maxLength: {
              value: 100,
              message: 'Name should be less than 100 characters.',
            },
          },
        },
        {
          name: 'creditCard.cardNetwork',
          label: 'Card Network',
          type: 'autocomplete',
          items: Object.entries(CardNetworksList).map(([value, label]) => ({
            value,
            label,
          })),
        },

        {
          name: 'creditCard.issuerBank',
          label: 'Issuer Bank',
          type: 'string',
          rules: {
            maxLength: {
              value: 50,
              message: 'Issuer Bank be less than 50 characters.',
            },
          },
        },
        {
          name: 'creditCard.lastFour',
          label: 'Last 4 Digits of card',
          type: 'number',
          rules: {
            maxLength: {
              value: 4,
              message: 'Must contain 4 number',
            },
            minLength: {
              value: 4,
              message: 'Must contain 4 number',
            },
          },
        },
      ],
    },
    {
      key: 'explosive',
      condition: PropertyTypePhysicalList.get('EXPLOSIVES')?.key,
      items: [
        {
          name: 'explosive.type',
          label: 'Type',
          type: 'autocomplete',
          items: Object.entries(ExplosiveTypesList).map(([value, label]) => ({
            value,
            label,
          })),
        },
        {
          name: 'explosive.quantity',
          label: 'Quantity',
          type: 'number',
        },
      ],
    },
    {
      key: 'cash',
      condition: PropertyTypePhysicalList.get('MONEY')?.key,
      items: [
        {
          name: 'cash.amount',
          label: 'Amount',
          type: 'number',
        },
        {
          name: 'cash.currency',
          label: 'Currency',
          type: 'select',
          defaultValue: CurrencyCode.USD,
          items: Object.entries(CurrencyCode).map(([value, label]) => ({
            value,
            label,
          })),
        },
      ],
    },
    {
      key: 'drugs',
      condition: PropertyTypePhysicalList.get('DRUGS_NARCOTICS')?.key,
      multiple: true,
      items: [],
    },
    {
      key: 'jewelry',
      condition: PropertyTypePhysicalList.get('JEWELRY_PRECIOUS_METALS')?.key,
      items: [
        {
          name: 'jewelry.type',
          label: 'Jewelry Type',
          type: 'autocomplete',
          items: 'getJewelryTypes',
          freeSolo: true,
          showPopupIcon: true,
        },
      ],
    },
    {
      key: 'knife',
      condition: PropertyTypePhysicalList.get('OTHER_WEAPONS')?.key,
      items: [
        {
          name: 'knife.make',
          label: 'Make',
          type: 'autocomplete',
          items: 'getKnifeMakes',
          freeSolo: true,
          showPopupIcon: true,
        },
        {
          name: 'knife.model',
          label: 'Model',
          type: 'autocomplete',
          items: 'getKnifeModels',
          clearOnBlur: false,
          autoHighlight: true,
          onKeyDown: preventSubmit,
          freeSolo: true,
          showPopupIcon: true,
        },
      ],
    },
    {
      key: 'firearm',
      condition: PropertyTypePhysicalList.get('FIREARMS')?.key,
      items: [
        {
          name: 'firearm.type',
          label: 'Firearm type',
          type: 'autocomplete',
          items: Object.entries(FirearmTypes).map(([value, label]) => ({
            value,
            label,
          })),
        },
        {
          name: 'firearm.make',
          label: 'Make',
          type: 'autocomplete',
          items: 'getFirearmMakes',
          freeSolo: true,
          showPopupIcon: true,
        },
        {
          name: 'firearm.model',
          label: 'Model',
          type: 'autocomplete',
          clearOnBlur: false,
          freeSolo: true,
          showPopupIcon: true,
          items: 'getFirearmModels',
          autoHighlight: true,
          onKeyDown: preventSubmit,
        },
      ],
    },
  ];
};

export const kasePropertyVehiclesTableColumns: TableColumn[] = [
  { key: 'masterVehicleLicensePlate', label: 'License Plate Number' },
  { key: 'masterVehicle.color', label: 'Color' },
  { key: 'masterVehicle.make', label: 'Make' },
  { key: 'masterVehicle.model', label: 'Model' },
  { key: 'masterVehicle.isVerified', label: 'Data Source' },
];

export const kasePropertyTowingTableColumns: TableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'phoneNumber', label: 'Phone number' },
  { key: 'address.addressLabel', label: 'Address' },
  { key: 'contactPerson', label: 'Contact person' },
  { key: 'contactPersonPhoneNumber', label: 'Contact person Phone number' },
];

export const nesterRowColumns = (
  PropertyType: Map<string, IOffenseOptionItem>,
) => {
  if (!PropertyType) {
    throw new Error('Property type not found.');
  }
  return {
    [PropertyType.get('ALCOHOL')?.key || 'ALCOHOL']: [
      { label: 'Brand', value: 'brand.name' },
      { label: 'Quantity', value: 'quantity' },
      {
        label: 'Measurement Unit',
        value: [AlcoholMeasurementTypeList, 'measurementType'],
      },
    ],
    [PropertyType.get('MONEY')?.key || 'MONEY']: [
      { label: 'Amount', value: 'amount' },
      { label: 'Currency', value: 'currency' },
    ],

    [PropertyType.get('CREDIT_DEBIT_CARDS')?.key || 'CREDIT_DEBIT_CARDS']: [
      { label: 'Name', value: 'name' },
      { label: 'Card Network', value: 'cardNetwork' },
      { label: 'Issuer Bank', value: 'issuerBank' },
      { label: 'Last 4 Digits of card', value: 'lastFour' },
    ],
    [PropertyType.get('DRUGS_NARCOTICS')?.key || 'DRUGS_NARCOTICS']: [
      { label: 'Types', value: 'type' },
      { label: 'Quantity', value: 'quantity' },
      { label: 'Measurement Unit', value: 'measurementType' },
      { label: 'Data Source', value: 'dataSource' },
    ],
    [PropertyType.get('EXPLOSIVES')?.key || 'EXPLOSIES']: [
      { label: 'Type', value: [ExplosiveTypesList, 'type'] },
      { label: 'Quantity', value: 'quantity' },
    ],
    [PropertyType.get('JEWELRY_PRECIOUS_METALS')?.key ||
    'JEWELRY_PRECIOUS_METALS']: [{ label: 'Jewelry type', value: 'type.type' }],
    [PropertyType.get('FIREARMS')?.key || 'FIREARMS']: [
      { label: 'Type', value: [FirearmTypes, 'type'] },
      { label: 'Make', value: 'make.name' },
      { label: 'Model', value: 'model.name' },
    ],
    [PropertyType.get('OTHER_WEAPONS')?.key || 'OTHER_WEAPONS']: [
      { label: 'Make', value: 'make.name' },
      { label: 'Model', value: 'model.name' },
    ],
    [PropertyType.get('AUTOMOBILES')?.key || 'AUTOMOBILES']: [
      { label: 'License Plate', value: 'masterVehicleLicensePlate' },
      { label: 'Color', value: 'masterVehicle.color' },
      { label: 'Make', value: 'masterVehicle.make' },
      { label: 'Model', value: 'masterVehicle.model' },
    ],
  };
};

export const PropertyViewFields: IViewCard[] = [
  {
    title: 'General information',
    key: 'generalInformation',
    size: { sm: 3 },
    items: [
      {
        label: 'Property type tag',
        value: 'typeTag',
      },
      {
        label: 'Name',
        value: 'name',
      },
      {
        label: 'Item number',
        value: 'itemNumber',
      },
      {
        label: 'Involvement',
        value: 'involvement',
      },
      {
        label: 'Item owner',
        value: 'owner.name',
      },
      {
        label: 'Target disposition date',
        value: 'targetDispositionDate',
      },
      {
        label: 'Serial number',
        value: 'serialNumber',
      },
      {
        label: 'Value',
        value: 'value',
      },
      {
        label: 'Description',
        value: 'description',
      },
      {
        label: 'Is evidence',
        value: 'isEvidence',
      },
      {
        label: 'Biohazard',
        value: 'isBiohazard',
      },
      {
        label: 'In police custody',
        value: 'status',
      },
      {
        label: 'Quantity',
        value: 'quantity',
      },
    ],
  },
  {
    title: 'Property type details',
    key: 'propertyTypeDetails',
    size: { md: 3 },
    items: [
      {
        label: 'Plate number',
        value: 'vehicle.masterVehicle.plateNumber',
      },
      {
        label: 'Color',
        value: 'vehicle.masterVehicle.color',
      },
      {
        label: 'Make',
        value: 'vehicle.masterVehicle.make',
      },
      {
        label: 'Model',
        value: 'vehicle.masterVehicle.model',
      },
      {
        label: 'involvement',
        value: 'vehicle.involvement',
      },
    ],
  },
  {
    title: 'Collection details',
    key: 'loggedInfo',
    size: { md: 2 },
    items: [
      {
        label: 'Collected by',
        value: 'loggedInfo.loggedBy.fullName',
      },
      {
        label: 'Collected on',
        value: 'loggedInfo.loggedDate',
      },
      {
        label: 'Location collected',
        value: 'loggedInfo.loggedAddress.normalized',
      },
      {
        label: 'Collected at incident location',
        value: 'loggedInfo.isLoggedAtKaseAddress',
      },
      {
        label: 'Transferred by',
        value: 'inboundTransferredInfo.transferredBy.fullName',
      },
      {
        label: 'Transferred on',
        value: 'inboundTransferredInfo.transferredDate',
      },
      {
        label: 'Check-in inventory location',
        value: 'droppedOffLocation.name',
      },
    ],
  },
];
